/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {defineMessage, useIntl} from 'react-intl'
import {Box, Container, Heading, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import AddressFields from '/overrides/app/components/forms/address-fields'
import FormActionButtons from '/overrides/app/components/forms/form-action-buttons'
import {MESSAGE_PROPTYPE} from '@salesforce/retail-react-app/app/utils/locale'
import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'
import Button from '/overrides/app/components/button'

const ShippingAddressForm = ({
    title,
    hasSavedAddresses,
    toggleAddressEdit,
    form,
    submitButtonLabel,
    formTitleAriaLabel,
    isNewAddress
}) => {
    const {tShipping} = useTranslationHelpers()
    const saveButtonMessage = tShipping?.buttonSaveAndContinue
    return (
        <Box
            {...(hasSavedAddresses && {
                gridColumn: [1, 1, 'span 2'],
                paddingTop: 6
            })}
            data-testid="sf-shipping-address-form"
        >
            <Stack spacing={6}>
                {hasSavedAddresses && (
                    <Heading as="h3" size="md">
                        {title}
                    </Heading>
                )}

                <Stack spacing={6}>
                    <AddressFields
                        form={form}
                        formTitleAriaLabel={formTitleAriaLabel}
                        isNewAddress={isNewAddress}
                    />

                    {hasSavedAddresses ? (
                        <FormActionButtons
                            saveButtonLabel={saveButtonMessage}
                            onCancel={toggleAddressEdit}
                            saveButtonProps={{
                                disabled: form.formState.isSubmitting,
                                isLoading: form.formState.isSubmitting
                            }}
                        />
                    ) : (
                        <Box>
                            <Container variant="form">
                                <Button
                                    type="submit"
                                    width="full"
                                    disabled={form.formState.isSubmitting}
                                >
                                    {submitButtonLabel}
                                </Button>
                            </Container>
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

ShippingAddressForm.propTypes = {
    title: PropTypes.string,
    hasSavedAddresses: PropTypes.bool,
    toggleAddressEdit: PropTypes.func,
    form: PropTypes.object,
    submitButtonLabel: PropTypes.string,
    formTitleAriaLabel: PropTypes.string
}

export default ShippingAddressForm
